import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Card,
  CardContent,
  Typography,
  Container,
  IconButton,
  Stack,
  FormHelperText,
  Chip,
  Alert,
  Box,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import EventIcon from "@mui/icons-material/Event";
import VaccinesIcon from "@mui/icons-material/Vaccines";

export default function PregnancyTracker({ cattleList, onNavigateToCattle, onNavigateToEvents }) {
  const { t } = useTranslation();
  const [selectedCattle, setSelectedCattle] = useState("");
  const [eventType, setEventType] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [pregnancyRecords, setPregnancyRecords] = useState(() => {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];
    const vaccinationDate = new Date(today);
    vaccinationDate.setDate(today.getDate() - 29);
    const aiDate = new Date(today);
    aiDate.setDate(today.getDate() - 275);
    const dueDateCalc = new Date(today);
    dueDateCalc.setDate(today.getDate() + 5);
    return [
      { id: 1, cattleId: 1, tagNumber: "COW001", nickname: "Bella", eventType: "ai", eventDate: formatDate(aiDate), dueDate: formatDate(dueDateCalc), notes: "First AI attempt", createdAt: formatDate(aiDate) + "T10:00:00.000Z" },
      { id: 2, cattleId: 2, tagNumber: "COW002", nickname: "Molly", eventType: "pd_test", eventDate: formatDate(new Date(today.getFullYear(), 1, 10)), dueDate: "", notes: "Pregnancy confirmed", createdAt: formatDate(new Date(today.getFullYear(), 1, 10)) + "T11:30:00.000Z" },
      { id: 3, cattleId: 3, tagNumber: "BUF001", nickname: "Daisy", eventType: "vaccination", eventDate: formatDate(vaccinationDate), dueDate: "", notes: "Routine vaccination", createdAt: formatDate(vaccinationDate) + "T09:15:00.000Z" },
      { id: 4, cattleId: 4, tagNumber: "COW003", nickname: "Lucy", eventType: "ai", eventDate: formatDate(new Date(today.getFullYear(), 3, 12)), dueDate: formatDate(new Date(today.getFullYear(), 0, 19)), notes: "Second AI attempt", createdAt: formatDate(new Date(today.getFullYear(), 3, 12)) + "T14:20:00.000Z" },
      { id: 5, cattleId: 5, tagNumber: "COW004", nickname: "Maggie", eventType: "checkup", eventDate: formatDate(new Date(today.getFullYear(), 4, 8)), dueDate: "", notes: "Regular health checkup", createdAt: formatDate(new Date(today.getFullYear(), 4, 8)) + "T16:45:00.000Z" },
      { id: 6, cattleId: 6, tagNumber: "BUF002", nickname: "Nelly", eventType: "pd_test", eventDate: formatDate(new Date(today.getFullYear(), 5, 15)), dueDate: "", notes: "Pregnancy test negative", createdAt: formatDate(new Date(today.getFullYear(), 5, 15)) + "T13:10:00.000Z" },
      { id: 7, cattleId: 7, tagNumber: "COW005", nickname: "Sophie", eventType: "vaccination", eventDate: formatDate(new Date(today.getFullYear(), 6, 20)), dueDate: "", notes: "Booster vaccination", createdAt: formatDate(new Date(today.getFullYear(), 6, 20)) + "T10:30:00.000Z" },
      { id: 8, cattleId: 8, tagNumber: "COW006", nickname: "Annie", eventType: "ai", eventDate: formatDate(new Date(today.getFullYear(), 7, 25)), dueDate: formatDate(new Date(today.getFullYear(), 5, 2)), notes: "AI with imported semen", createdAt: formatDate(new Date(today.getFullYear(), 7, 25)) + "T15:00:00.000Z" },
      { id: 9, cattleId: 9, tagNumber: "BUF003", nickname: "Betty", eventType: "delivery", eventDate: formatDate(new Date(today.getFullYear(), 8, 10)), dueDate: "", notes: "Successful delivery of calf", createdAt: formatDate(new Date(today.getFullYear(), 8, 10)) + "T08:45:00.000Z" },
      { id: 10, cattleId: 10, tagNumber: "COW007", nickname: "Cathy", eventType: "checkup", eventDate: formatDate(new Date(today.getFullYear(), 9, 5)), dueDate: "", notes: "Post-delivery checkup", createdAt: formatDate(new Date(today.getFullYear(), 9, 5)) + "T12:15:00.000Z" }
    ];
  });
  const [nextId, setNextId] = useState(11);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });

  // Calculate alerts based on pregnancy records
  useEffect(() => {
    const today = new Date();
    const newAlerts = [];

    pregnancyRecords.forEach(record => {
      if (record.dueDate) {
        const dueDate = new Date(record.dueDate);
        const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

        if (daysUntilDue <= 7 && daysUntilDue >= 0) {
          newAlerts.push({
            type: "due_date",
            message: `${record.tagNumber} - Due date in ${daysUntilDue} days`,
            severity: "error",
            cattleId: record.cattleId
          });
        }
      }

      if (record.eventDate && record.eventType === "vaccination") {
        const eventDate = new Date(record.eventDate);
        const daysSinceEvent = Math.ceil((today - eventDate) / (1000 * 60 * 60 * 24));

        if (daysSinceEvent >= 28 && daysSinceEvent <= 30) {
          const nextDueDate = new Date(eventDate);
          nextDueDate.setDate(eventDate.getDate() + 30);
          const formatDate = (date) => date.toISOString().split('T')[0];
          newAlerts.push({
            type: "vaccination",
            message: `${record.tagNumber} - Next vaccination due on ${formatDate(nextDueDate)}`,
            severity: "warning",
            cattleId: record.cattleId
          });
        }
      }
    });

    setAlerts(newAlerts);
  }, [pregnancyRecords]);

  // Auto-navigate to last page when records change
  useEffect(() => {
    const totalPages = Math.ceil(pregnancyRecords.length / paginationModel.pageSize);
    const lastPage = Math.max(0, totalPages - 1);
    if (paginationModel.page !== lastPage) {
      setPaginationModel(prev => ({ ...prev, page: lastPage }));
    }
  }, [pregnancyRecords.length, paginationModel.pageSize]);

  const resetForm = () => {
    setSelectedCattle("");
    setEventType("");
    setEventDate("");
    setDueDate("");
    setNotes("");
    setEditingId(null);
    setErrors({});
  };

  const validate = () => {
    const temp = {};
    if (!selectedCattle) temp.selectedCattle = t("pregnancyTracker.pleaseSelectCattle");
    if (!eventType) temp.eventType = t("pregnancyTracker.eventTypeRequired");
    if (!eventDate) temp.eventDate = t("pregnancyTracker.eventDateRequired");
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const calculateDueDate = (eventType, eventDate) => {
    if (eventType === "ai" && eventDate) {
      const date = new Date(eventDate);
      date.setDate(date.getDate() + 280); // 280 days gestation for cattle
      return date.toISOString().split('T')[0];
    }
    return "";
  };

  const handleEventTypeChange = (e) => {
    const newEventType = e.target.value;
    setEventType(newEventType);

    if (newEventType === "ai" && eventDate) {
      setDueDate(calculateDueDate(newEventType, eventDate));
    }
  };

  const handleEventDateChange = (e) => {
    const newEventDate = e.target.value;
    setEventDate(newEventDate);

    if (eventType === "ai" && newEventDate) {
      setDueDate(calculateDueDate(eventType, newEventDate));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const cattle = cattleList.find(c => c.id === parseInt(selectedCattle));
    const payload = {
      id: editingId ?? nextId,
      cattleId: parseInt(selectedCattle),
      tagNumber: cattle?.tagNumber || "",
      nickname: cattle?.nickname || "",
      eventType,
      eventDate,
      dueDate: eventType === "ai" ? dueDate : "",
      notes,
      createdAt: new Date().toISOString()
    };

    let newRecords;
    if (editingId) {
      newRecords = pregnancyRecords.map(r => r.id === editingId ? payload : r);
    } else {
      newRecords = [...pregnancyRecords, payload];
    }

    setPregnancyRecords(newRecords);
    setNextId(editingId ? nextId : nextId + 1);

    resetForm();
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setSelectedCattle(row.cattleId.toString());
    setEventType(row.eventType);
    setEventDate(row.eventDate);
    setDueDate(row.dueDate || "");
    setNotes(row.notes || "");
  };

  const handleDelete = (id) => {
    setPregnancyRecords(prev => prev.filter(r => r.id !== id));
    if (editingId === id) resetForm();
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case "due_date": return <WarningIcon />;
      case "vaccination": return <VaccinesIcon />;
      default: return <EventIcon />;
    }
  };

  const columns = [
    { field: "serial", headerName: "#", width: 70 },
    { field: "tagNumber", headerName: t("cattleForm.tagNumber"), flex: 1, minWidth: 120 },
    { field: "nickname", headerName: t("cattleForm.nickname"), flex: 1, minWidth: 120 },
    { field: "eventType", headerName: t("pregnancyTracker.eventType"), flex: 1, minWidth: 120,
      renderCell: (params) => {
        const type = params.value;
        return t(`pregnancyTracker.eventTypes.${type}`) || type;
      }
    },
    { field: "eventDate", headerName: t("pregnancyTracker.eventDate"), flex: 1, minWidth: 120 },
    { field: "dueDate", headerName: t("pregnancyTracker.expectedDueDate"), flex: 1, minWidth: 120,
      renderCell: (params) => {
        if (!params.value) return "-";
        const today = new Date();
        const dueDate = new Date(params.value);
        const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

        if (daysUntilDue <= 7 && daysUntilDue >= 0) {
          return <Chip label={`${params.value} (${daysUntilDue}d)`} color="error" size="small" />;
        }
        return params.value;
      }
    },
    {
      field: "actions",
      headerName: t("actions.edit"),
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton color="primary" size="small" onClick={() => handleEdit(params.row)} aria-label={t("actions.edit")}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton color="error" size="small" onClick={() => handleDelete(params.row.id)} aria-label={t("actions.delete")}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const rows = pregnancyRecords.map((r, idx) => ({
    id: r.id,
    serial: idx + 1,
    cattleId: r.cattleId,
    tagNumber: r.tagNumber,
    nickname: r.nickname,
    eventType: r.eventType,
    eventDate: r.eventDate,
    dueDate: r.dueDate,
    notes: r.notes
  }));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold', color: '#333', textAlign: 'center' }}>
        🤰 {t("pregnancyTracker.title")}
      </Typography>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="error">
              <WarningIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
              {t("pregnancyTracker.alertsCount", { count: alerts.length })}
            </Typography>
            <Stack spacing={1}>
              {alerts.map((alert, index) => (
                <Alert key={index} severity={alert.severity} icon={getAlertIcon(alert.type)}>
                  {alert.message}
                </Alert>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3} justifyContent="center">
        {/* Pregnancy Event Form */}
        <Grid item xs={12} md={8} lg={6}>
          <Card elevation={3} sx={{
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom sx={{
                fontWeight: 'bold',
                color: '#333',
                textAlign: 'center',
                mb: 3,
                fontSize: '1.6rem'
              }}>
                🤰 {editingId ? t("pregnancyTracker.editEvent") : t("pregnancyTracker.addEvent")}
              </Typography>
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  {/* Cattle Selection */}
                  <FormControl fullWidth error={!!errors.selectedCattle}>
                    <InputLabel>{t("pregnancyTracker.cattle")}</InputLabel>
                    <Select
                      value={selectedCattle}
                      label={t("pregnancyTracker.cattle")}
                      onChange={(e) => setSelectedCattle(e.target.value)}
                    >
                      {cattleList.length === 0 ? (
                        <MenuItem value="" disabled>
                          No cattle available - Add cattle first in Cattle Management
                        </MenuItem>
                      ) : (
                        cattleList.map(cattle => (
                          <MenuItem key={cattle.id} value={cattle.id}>
                            {cattle.tagNumber} - {cattle.nickname || "No name"} ({cattle.animalType})
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {errors.selectedCattle && <FormHelperText>{errors.selectedCattle}</FormHelperText>}
                    {cattleList.length === 0 && (
                      <FormHelperText>
                        Please add cattle records first in the Cattle Management tab
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* Event Type */}
                  <FormControl fullWidth error={!!errors.eventType}>
                    <InputLabel>{t("pregnancyTracker.eventType")}</InputLabel>
                    <Select
                      value={eventType}
                      label={t("pregnancyTracker.eventType")}
                      onChange={handleEventTypeChange}
                    >
                      <MenuItem value="ai">{t("pregnancyTracker.eventTypes.ai")}</MenuItem>
                      <MenuItem value="pd_test">{t("pregnancyTracker.eventTypes.pd_test")}</MenuItem>
                      <MenuItem value="vaccination">{t("pregnancyTracker.eventTypes.vaccination")}</MenuItem>
                      <MenuItem value="checkup">{t("pregnancyTracker.eventTypes.checkup")}</MenuItem>
                      <MenuItem value="delivery">{t("pregnancyTracker.eventTypes.delivery")}</MenuItem>
                    </Select>
                    {errors.eventType && <FormHelperText>{errors.eventType}</FormHelperText>}
                  </FormControl>

                  {/* Event Date */}
                  <TextField
                    fullWidth
                    label={t("pregnancyTracker.eventDate")}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={eventDate}
                    onChange={handleEventDateChange}
                    error={!!errors.eventDate}
                    helperText={errors.eventDate}
                  />

                  {/* Due Date (auto-calculated for AI) */}
                  {eventType === "ai" && dueDate && (
                    <TextField
                      fullWidth
                      label={t("pregnancyTracker.expectedDueDate")}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={dueDate}
                      disabled
                      helperText="Calculated automatically (280 days gestation)"
                    />
                  )}

                  {/* Notes */}
                  <TextField
                    fullWidth
                    label={t("pregnancyTracker.notes")}
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />

                  {/* Buttons */}
                  <Stack direction="row" spacing={2}>
                    <Button variant="contained" color="primary" type="submit" fullWidth>
                      {editingId ? t("pregnancyTracker.updateEvent") : t("pregnancyTracker.saveEvent")}
                    </Button>
                    {editingId && (
                      <Button variant="outlined" onClick={resetForm} fullWidth>
                        {t("pregnancyTracker.cancel")}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Pregnancy Records */}
        <Grid item xs={12} md={8} lg={6} sx={{ overflowX: 'auto' }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pregnancy Records ({pregnancyRecords.length})
              </Typography>
          <Box sx={{ height: 400, minWidth: 600 }}>
            <DataGrid
              key={`page-${paginationModel.page}-${pregnancyRecords.length}`}
              rows={rows}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25]}
              disableSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  fontSize: '0.9rem',
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '0.85rem',
                },
                '@media (max-width: 600px)': {
                  minWidth: '100%',
                  '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
                    fontSize: '0.75rem',
                    padding: '4px 8px',
                  },
                },
              }}
            />
          </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
